import React, { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { boolean, InferType, object, string } from 'yup';

import { Client, PersonOrgProfile } from '@ndvr/customer-api';
import { Account, CustodyType } from '@ndvr/portfolio-api';

import { FormSheet } from '@/components/FormSheet';
import { getPersonOrgName, useRequiredParams } from 'utils';
import { useClientProfilesForHousehold, useCreateClient, useLinkCustodyAccount } from 'views/client/client-queries';
import {
    custodyAccountIdValidator,
    FormInput,
    FormSelect,
    normalizeCustodyAccountId,
    requiredStringValidator,
    Toast,
} from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { useCommandCenterHouseholdProfile } from '../household-queries';

const formSchema = object({
    custodyType: requiredStringValidator.oneOf(Object.values(CustodyType)),
    custodyAccount: custodyAccountIdValidator('custodyType'),
    personOrgGUID: requiredStringValidator,
    needsClient: boolean(),
    startDate: string().when('needsClient', {
        is: true,
        then: (s) => s.required('Start date is required'),
        otherwise: (s) => s.optional(),
    }),
});

type FormType = InferType<typeof formSchema>;

export const LinkAccountDrawer = ({ account, onClose }: { account: Account; onClose: () => void }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: householdProfile } = useCommandCenterHouseholdProfile(globalAccountGUID);
    const { data: clientProfiles } = useClientProfilesForHousehold(globalAccountGUID);
    const { mutate: linkCustodyAccount, isPending } = useLinkCustodyAccount();
    const { mutateAsync: createClientAsync, isPending: isCreatingClient } = useCreateClient(globalAccountGUID);

    const clientsByPersonOrgGUID = useMemo(
        () => new Map<string, Client>(clientProfiles?.map(({ client }) => [client.personOrgGUID, client]) ?? []),
        [clientProfiles],
    );

    const members = householdProfile?.personOrgFinancialProfiles ?? [];

    const onSubmit = async (formValues: FormType, close: () => void) => {
        let clientGUID = clientsByPersonOrgGUID.get(formValues.personOrgGUID)?.clientGUID;

        if (!clientGUID) {
            const client = await createClientAsync({
                personOrgGUID: formValues.personOrgGUID,
                startDate: formValues.startDate!,
            });
            clientGUID = client?.clientGUID;
        }

        if (!clientGUID) {
            Toast.error('Failed to resolve account owner. Please contact support.', 'error');
            return;
        }

        linkCustodyAccount(
            {
                request: {
                    custodyType: formValues.custodyType,
                    custodyAccountId: formValues.custodyAccount,
                },
                account,
                globalAccountGUID,
                clientGUID,
            },
            { onSuccess: close },
        );
    };

    return (
        <FormSheet
            title={`Link ${account.accountName.plainText}`}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={formSchema}
            pending={isPending || isCreatingClient}
            submitLabel="Link"
        >
            <LinkAccountFields members={members} clientsByPersonOrgGUID={clientsByPersonOrgGUID} />
        </FormSheet>
    );
};

const LinkAccountFields = ({
    members,
    clientsByPersonOrgGUID,
}: {
    members: { profile: PersonOrgProfile }[];
    clientsByPersonOrgGUID: Map<string, Client>;
}) => {
    const { setValue } = useFormContext();
    const [personOrgGUID] = useWatch({ name: ['personOrgGUID'] });

    const needsClient = personOrgGUID ? !clientsByPersonOrgGUID.has(personOrgGUID) : false;

    useEffect(() => {
        setValue('needsClient', needsClient);
    }, [needsClient, setValue]);

    return (
        <>
            <FormSelect name="personOrgGUID" label="Account Owner">
                {members.map(({ profile }) => (
                    <option key={profile.personOrgGUID} value={profile.personOrgGUID}>
                        {getPersonOrgName(profile.personOrg)}
                    </option>
                ))}
            </FormSelect>
            <FormSelect label="Custodian" name="custodyType">
                <option value={CustodyType.FIDELITY}>Fidelity</option>
                <option value={CustodyType.SCHWAB}>Charles Schwab</option>
                <option value={CustodyType.IB}>Interactive Brokers</option>
            </FormSelect>
            <FormInput
                label="Custody Account"
                name="custodyAccount"
                placeholder="account id"
                normalize={normalizeCustodyAccountId}
            />
            {needsClient && <FormDateInput name="startDate" label="Client agreement start date" />}
        </>
    );
};
